import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import { OneGraphProvider } from '@oneaudi/onegraph-client';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import React from 'react';
import styled from 'styled-components';
import { FeatureApp } from './FeatureApp';
import { CompareContextProvider } from './context/CompareContext';
import { ContentContextProvider, useContent } from './context/ContentContext';
import { DataContextProvider } from './context/DataContext';
import type { FeatureAppEnvContextValue } from './context/FeatureAppEnvContext';
import { FeatureAppEnvContext } from './context/FeatureAppEnvContext';
import { UniversalStateContext } from './context/UniversalStateContext';
import { FilterProvider } from './context/FilterContext';
import { lightTheme, darkTheme } from './theme';
import type { UniversalState } from './utils/createUniversalState';
import { getThemeName } from './utils/alphaHelpers';

const Wrapper = styled.div`
  max-width: 1920px;
  margin: 0 auto;
`;

interface FeatureAppWrapperProps {
  readonly env: FeatureAppEnvContextValue;
  readonly universalState?: UniversalState;
}

const FilterAndFeatureAppWrapper = () => {
  const { preFilteredCarlineGroup } = useContent();
  return (
    <FilterProvider preFilteredCarlineGroup={preFilteredCarlineGroup}>
      <FeatureApp />
    </FilterProvider>
  );
};

export const FeatureAppWrapper: React.FC<FeatureAppWrapperProps> = ({
  env,
  universalState,
}: FeatureAppWrapperProps) => {
  const oneGraphClient = env.featureServices['onegraph-service'].getApolloClient();
  const contentService = env.featureServices['audi-content-service'];
  const footnoteReferenceService =
    env.featureServices['audi-footnote-reference-service']?.getDefaultScopeRefService();
  const fefaID = footnoteReferenceService.getConsumerId();
  const renderModeService = env.featureServices['audi-render-mode-service'];
  const selectedTheme = getThemeName() === 'dark' ? darkTheme : lightTheme;

  return (
    <UniversalEditorProvider contentService={contentService} renderModeService={renderModeService}>
      <ThemeProvider theme={selectedTheme}>
        <FeatureAppEnvContext.Provider value={env}>
          <ContentContextProvider contentService={contentService}>
            <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
              <Wrapper data-fefa-custom-id={fefaID}>
                <OneGraphProvider client={oneGraphClient}>
                  <DataContextProvider localeService={env.featureServices['gfa:locale-service']}>
                    <UniversalStateContext.Provider value={universalState}>
                      <CompareContextProvider>
                        <FilterAndFeatureAppWrapper />
                      </CompareContextProvider>
                    </UniversalStateContext.Provider>
                  </DataContextProvider>
                </OneGraphProvider>
              </Wrapper>
            </FootnoteContextProvider>
          </ContentContextProvider>
        </FeatureAppEnvContext.Provider>
      </ThemeProvider>
    </UniversalEditorProvider>
  );
};
